import { useEffect, useRef, useState } from 'react';
import { Search } from 'lucide-react';
import {
  BUG_PRIORITIES,
  BUG_STATUSES,
  type BugFilters,
  type BugReportPriority,
  type BugReportStatus,
} from '../../lib/bugReportsApi';
import { Select } from '../ui/Select';
import { useBugLabels } from './bugVisuals';
import { useI18n } from '../../contexts/I18nContext';

interface Props {
  filters: BugFilters;
  onChange: (patch: Partial<BugFilters>) => void;
  onClear: () => void;
}

export function BugReportsFilters({ filters, onChange, onClear }: Props) {
  const { t } = useI18n();
  const labels = useBugLabels();

  // Debounced search, like the tasks list: typing must not fire a request per
  // keystroke. Seeded from the prop so "clear filters" empties the box too.
  const [search, setSearch] = useState(filters.q ?? '');
  useEffect(() => {
    setSearch(filters.q ?? '');
  }, [filters.q]);
  // `onChange` is recreated on every parent render, so it is deliberately kept
  // out of the dependency list through a ref: depending on it would restart the
  // timer each render and the search would never fire.
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;
  useEffect(() => {
    if (search === (filters.q ?? '')) return;
    const id = window.setTimeout(() => onChangeRef.current({ q: search || undefined }), 300);
    return () => window.clearTimeout(id);
  }, [search, filters.q]);

  const hasFilters = Boolean(filters.q || filters.status || filters.priority);

  return (
    <div className="flex flex-col gap-3 sm:flex-row sm:items-end">
      <label className="relative block w-full sm:max-w-xs">
        <span className="ui-field-label">{t('bugReports.search')}</span>
        <Search
          size={15}
          className="pointer-events-none absolute bottom-2.5 left-2.5 text-gray-400"
        />
        <input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder={t('bugReports.search')}
          className="ui-input mt-1 w-full pl-8"
        />
      </label>

      <Select
        label={t('bugReports.column.status')}
        clearable
        value={filters.status ?? ''}
        onChange={(value) => onChange({ status: (value || undefined) as BugReportStatus })}
        placeholder={t('bugReports.allStatuses')}
        options={BUG_STATUSES.map((status) => ({ value: status, label: labels.status(status) }))}
        className="w-full sm:w-44"
      />

      <Select
        label={t('bugReports.column.priority')}
        clearable
        value={filters.priority ?? ''}
        onChange={(value) => onChange({ priority: (value || undefined) as BugReportPriority })}
        placeholder={t('bugReports.allPriorities')}
        options={BUG_PRIORITIES.map((priority) => ({
          value: priority,
          label: labels.priority(priority),
        }))}
        className="w-full sm:w-44"
      />

      {hasFilters && (
        <button
          type="button"
          onClick={onClear}
          className="self-start pb-2 text-sm text-brand-700 hover:underline sm:self-end"
        >
          {t('bugReports.clearFilters')}
        </button>
      )}
    </div>
  );
}
